class MoodboardShare extends HTMLElement {
  constructor() {
    super();

    this.activeCustomerId = this.dataset.activeCustomerId;
    this.canvas = null;

    this.getUrlParams();
    this.getElements();
    this.applyWishlistWording();

    this.getMoodboard()
      .then(data => {
        this.moodboard = {
          id: this.moodboardId,
          title: data.moodboard.title,
          ...data.moodboard.mood_board,
          defaultBackgroundImage: data.moodboard.default_background_image,
        };

        this.products = data.moodboard.products;

        this.replacePageTitle();

        if (this.hasShareAccess) {
          this.replaceReturnToEditLink();
        } else {
          this.removeHeader();
        }

        this.configureEmailUrl();
        this.setupListeners();
        this.populateProducts();

        if ((this.moodboard?.objects?.length ?? 0) > 0) {
          this.renderFullSizeMoodboardAsImage();
        }
      })
      .catch(error => {
        console.error(error)
      });
  }

  getUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);

    // Redirect user to homepage if required params are missing
    // TODO: Update this to display an error message instead
    if (!urlParams.has('cid') || !urlParams.has('id')) {
      window.location.href = '/';
      return;
    }

    this.moodboardCustomerId = urlParams.get('cid');
    this.moodboardId = urlParams.get('id');
    this.displayType = urlParams.get('display') || 'moodboard';
  }

  // Opened from Share wishlist (?display=wishlist): the invite heading, its line and
  // the email subject say wishlist rather than moodboard. The snippet carries the
  // wishlist wording in data attributes. Runs before configureEmailUrl, which builds
  // on the mailto href.
  applyWishlistWording() {
    if (this.displayType !== 'wishlist') {
      return;
    }

    this.querySelectorAll('[data-wishlist-text]').forEach(el => {
      if (el.dataset.wishlistText) {
        el.textContent = el.dataset.wishlistText;
      }
    });

    if (this.emailLinkEl && this.emailLinkEl.dataset.wishlistHref) {
      this.emailLinkEl.setAttribute('href', this.emailLinkEl.dataset.wishlistHref);
    }
  }

  getElements() {
    this.loadingEl = this.querySelector('.moodboard-share__loading');
    this.moodboardEl = this.querySelector('.moodboard-share__moodboard');
    this.copyLinkEl = this.querySelector('.moodboard-share header div button');
    this.copyLinkNotificationEl = this.querySelector('.moodboard-share__copy-link-notification');
    this.emailLinkEl = this.querySelector('.moodboard-share__send-by-email');
    this.addAllProductsEl = this.querySelector('.moodboard-share__quick-links button');
    this.productsEl = this.querySelector('.moodboard-share__products');
    this.canvasEl = this.querySelector('.moodboard-share__canvas__wrapper');
  }

  replacePageTitle() {
    const titleEl = document.querySelector('h1');

    if (titleEl) {
      titleEl.innerHTML = this.moodboard.title;
    }
  }

  replaceReturnToEditLink() {
    const returnToEditEl = document.querySelector('.moodboard-share__moodboard header > a');
    const editWishlistEl = document.querySelector('.moodboard-share__quick-links a');

    if (returnToEditEl) {
      returnToEditEl.href = `https://${window.routes.store_url}/pages/mood-board?id=${this.moodboardId}`;
    }

    if (editWishlistEl) {
      editWishlistEl.href = `https://${window.routes.store_url}/pages/mood-board?id=${this.moodboardId}`;
    }
  }

  removeHeader() {
    const headerEl = document.querySelector('.moodboard-share__moodboard header');
    const editQuickLinkEl = document.querySelector('.moodboard-share__quick-links a');

    if (headerEl) {
      headerEl.remove();
    }

    if (editQuickLinkEl) {
      editQuickLinkEl.remove();
    }
  }

  showMoodboard() {
    this.loadingEl.classList.add('hidden');
    this.moodboardEl.classList.remove('hidden');
  }

  getMoodboard() {
    return new Promise((resolve, reject) => {
      fetch(`https://${window.routes.store_url}/tools/mood-boards-app/mood-boards/${this.moodboardCustomerId}/${this.moodboardId}`)
        .then(response => response.json())
        .then(data => resolve(data))
        .catch(error => reject(error));
    });
  }

  configureEmailUrl() {
    let emailUrl = `${this.emailLinkEl.getAttribute('href')}&body=`;
    emailUrl += window.location.origin;
    emailUrl += window.location.pathname;
    emailUrl += encodeURIComponent(window.location.search);

    this.emailLinkEl.setAttribute('href', emailUrl);
  }

  setupListeners() {
    this.copyLinkEl.addEventListener('click', () => {
      navigator.clipboard.writeText(window.location.href);
      this.copyLinkNotificationEl.classList.add('visible');
      setTimeout(() => this.copyLinkNotificationEl.classList.remove('visible'), 3000);
    });

    if (this.products.length > 0) {
      this.addAllProductsEl.addEventListener('click', () => this.addAllProductsToCart());
      this.addAllProductsEl.removeAttribute('disabled');
    }
  }

  populateProducts() {
    if (this.products.length > 0) {
      let requests = [];

      this.products.forEach(product => {
        if (!product.handle) {
          return;
        }

        requests.push(fetch(`/products/${product.handle}`));
      });

      Promise.all(requests)
        .then(responses => {
          return Promise.all(responses.map(response => {
            if (response.ok) {
              return response.text();
            }
          }))
        })
        .then(products => {
          products.forEach((product) => {
            const parser = new DOMParser();
            const productHtml = parser.parseFromString(product, 'text/html');
            const moodboardProduct = productHtml.querySelector('.js-product-card');

            if (moodboardProduct) {
              this.productsEl.innerHTML += `
                <div class="moodboard-share__products__product">
                  ${moodboardProduct.innerHTML}
                </div>
              `
            }
          });

          this.showMoodboard();
        })
    } else {
      this.productsEl.innerHTML = '';
      this.showMoodboard();
    }
  }

  renderFullSizeMoodboardAsImage() {
    if (this.displayType === 'wishlist') {
      return;
    }

    const container = document.querySelector('.moodboard-share__canvas');

    if (!container) {
      return;
    }

    const offscreenCanvasEl = document.createElement('canvas');

    offscreenCanvasEl.width = 1604;
    offscreenCanvasEl.height = 802;
    offscreenCanvasEl.style.position = 'absolute';
    offscreenCanvasEl.style.left = '-9999px';

    document.body.appendChild(offscreenCanvasEl);

    const offscreenCanvas = new fabric.StaticCanvas(offscreenCanvasEl, {
      preserveObjectStacking: true,
    });

    offscreenCanvas.loadFromJSON(this.moodboard, () => {
      if (offscreenCanvas.backgroundImage) {
        offscreenCanvas.backgroundImage.scaleToWidth(offscreenCanvas.width);
        offscreenCanvas.backgroundImage.scaleToHeight(offscreenCanvas.height);
      }

      offscreenCanvas.renderAll();

      const dataUrl = offscreenCanvas.toDataURL({
        format: 'jpeg',
        quality: 0.8,
      });

      offscreenCanvasEl.remove();

      container.innerHTML = `<img src="${dataUrl}" alt="${this.moodboard.title}" />`;
    });
  }

  addAllProductsToCart() {
    const productCards = this.querySelectorAll('.product-card');
    const data = { items: [] };

    this.addAllProductsEl.setAttribute('disabled', 'disabled');

    productCards.forEach(el => {
      const id = el.querySelector('[name="id"]')?.value ?? null;

      if (!id) {
        return;
      }

      data.items.push({
        id,
        quantity: 1
      });
    })

    fetch(`${routes.cart_add_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
      .then(res => {
        if (!res.ok) {
          document.dispatchEvent(
            new CustomEvent('alert:show', {
              detail: {
                message: window.cartStrings.error,
                status: 'error',
              },
            })
          );
        } else {
          publish(PUB_SUB_EVENTS.cartAdd);
        }
      })
      .catch(e => console.error(e))
      .finally(() => this.addAllProductsEl.removeAttribute('disabled'))
  }

  get hasShareAccess() {
    return (this.activeCustomerId === this.moodboardCustomerId);
  }
}

customElements.define('moodboard-share', MoodboardShare);
